import type { DataProvider, Identifier } from 'react-admin';
import { hasAccess, type Capability, type Role } from '../auth/permissions';

/**
 * THE DECISION CHOKEPOINT (D-payout-boundary).
 *
 * Every action-style status transition — Approve/Reject a cashout, Confirm/Reject
 * a cashback — goes through here. It does exactly two things:
 *   1. GUARDS the transition against the acting role's capability. This is not
 *      just hidden UI: a transition attempted without the capability is rejected
 *      here, before any write. (The mock has no server; the real admin API MUST
 *      enforce the same check server-side — this is the interim client-side
 *      guard, mirroring the audit wrapper's interim/real split.)
 *   2. Writes ONLY the new status via the provider — a single, O(1) update. No
 *      payout, no simulation, no async/heavy work. The admin records an
 *      accountable DECISION; it never moves money itself. At launch a HUMAN
 *      operator executes the payout by hand and records the outcome here
 *      (D-cashouts-manual-execution, the web repo's ADR 0005); the automated
 *      PayoutEngine is deferred to the cross-repo cashblack-web. The update is
 *      wrapped by withAudit (#25), so the decision is automatically recorded
 *      (actor, status diff, region, resource, recordId) with no audit code at the
 *      call site.
 *
 * `resource` is the REAL underlying collection (`cashoutRequests` /
 * `transactions` / `users`), not the read-only virtual list resource — so the
 * write lands on the real row and the audit entry names the real entity.
 *
 * MOST transitions are a single `status` write: pass `fromStatus` / `toStatus`
 * and the chokepoint writes `{ status: toStatus }`. A few (the Subscriptions
 * actions, which set `plusStatus` plus companion fields like `autoRenew` on the
 * `users` row) need a multi-field write: pass an explicit `data` / `previousData`
 * patch instead. Either way it is one guarded, audited `update` and nothing more.
 * The audit wrapper diffs the patch, so a two-field write records a two-field
 * diff for free.
 */
export const performStatusTransition = async (params: {
  dataProvider: Pick<DataProvider, 'update'>;
  role: Role | null;
  capability: Capability;
  resource: string;
  id: Identifier;
  /** The status convenience: writes `{ status: toStatus }`. */
  fromStatus?: string;
  toStatus?: string;
  /** A general field patch (e.g. `{ plusStatus, autoRenew }`); overrides the
   *  status convenience when provided. */
  data?: Record<string, unknown>;
  previousData?: Record<string, unknown>;
}): Promise<void> => {
  const { dataProvider, role, capability, resource, id } = params;

  if (!hasAccess({ role, accessibleRegions: [] }, { capability })) {
    throw new Error(`Not permitted: ${capability}`);
  }

  const data = params.data ?? { status: params.toStatus };
  const previousData = params.previousData ?? {
    id,
    status: params.fromStatus,
  };

  await dataProvider.update(resource, { id, data, previousData });
};
